import { useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, Dialog, Tag } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { gnbDeployService, DeploySubmitPayload } from '../../services/gnbDeployService';

/**
 * Per-node info the review needs to summarize the upgrade accurately: which artifacts are
 * reused (already in RLO) vs. newly staged for upload in this session.
 */
export interface ReviewNode {
  id: string;
  nodeName: string;
  currentVersion?: number | null;
  hasStagedCiq?: boolean;
  hasStagedDay1?: boolean;
  /** credentials were entered/overridden for this node in this session */
  credsProvided: boolean;
}

interface DeployReviewDialogProps {
  nodes: ReviewNode[];
  deselectedIds: Set<string>;
  credentialsMap: Map<string, { username: string; password: string; isNew: boolean; overridden: boolean }>;
  ticketId: string;
  automationFlowId: string;
  deploymentId: string;
  workflowId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DeployReviewDialog({
  nodes,
  deselectedIds,
  credentialsMap,
  ticketId,
  automationFlowId,
  deploymentId,
  workflowId,
  onClose,
  onSuccess
}: DeployReviewDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const selected = nodes.filter((n) => !deselectedIds.has(n.id));

  const handleConfirm = async () => {
    setSubmitting(true);
    showLoader();
    try {
      const payload: DeploySubmitPayload = {
        ticketId,
        automationFlowId,
        deploymentId,
        workflowId,
        deselectedNodeIds: Array.from(deselectedIds),
        credentials: Array.from(credentialsMap.entries())
          .filter(([, creds]) => creds.username)
          .map(([nodeId, creds]) => ({ gnbNodeId: nodeId, username: creds.username, password: creds.password }))
      };
      await gnbDeployService.submit(payload);
      addNotifications([
        { title: 'Success', icon: 'icon-check', description: `${selected.length} node(s) submitted for upgrade.`, color: 'green', timestamp: new Date() }
      ]);
      onSuccess();
    } catch (err) {
      addNotifications([
        { title: 'Error', icon: 'icon-triangle-warning', description: `Failed to submit upgrade: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }
      ]);
    } finally {
      setSubmitting(false);
      setTimeout(() => hideLoader(), 500);
    }
  };

  // Roll-up: reused (already in RLO) vs. newly staged for this upgrade.
  const ciqNew = selected.filter((n) => n.hasStagedCiq).length;
  const day1New = selected.filter((n) => n.hasStagedDay1).length;
  const credsNew = selected.filter((n) => n.credsProvided).length;
  const ciqReuse = selected.length - ciqNew;
  const day1Reuse = selected.length - day1New;
  const credsReuse = selected.length - credsNew;

  const reuseNewTag = (isNew: boolean) =>
    isNew ? (
      <ReactEDSWrapper component={Tag} color="orange">New (staged)</ReactEDSWrapper>
    ) : (
      <ReactEDSWrapper component={Tag} color="green">Reuse</ReactEDSWrapper>
    );

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      show
      title={`Review Upgrade — ${ticketId}`}
      subtitle="CR gNB — Software Upgrade"
      onClose={onClose}
      bottom={
        <ReactEDSWrapper component={Button} primary onClick={handleConfirm} disabled={submitting || selected.length === 0}>
          {submitting ? 'Submitting…' : `Confirm Upgrade (${selected.length})`}
        </ReactEDSWrapper>
      }
    >
      <p style={{ marginBottom: 8 }}>
        <strong>{selected.length}</strong> node(s) will be upgraded.
      </p>
      <p style={{ fontSize: 12, color: '#8b8b8b', marginBottom: 16 }}>
        Existing CIQ &amp; Day-1 configuration in RLO is reused. Only artifacts staged in this session
        are uploaded as new. Credentials are reused unless newly entered.
      </p>

      {/* Roll-up summary */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
        <tbody>
          <tr>
            <td style={{ fontWeight: 600, padding: '6px 8px', width: 140 }}>CIQ</td>
            <td style={{ padding: '6px 8px', color: '#8b8b8b' }}>
              {ciqReuse} reuse existing{ciqNew > 0 ? `, ${ciqNew} new staged` : ''}
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: 600, padding: '6px 8px' }}>Day-1 config</td>
            <td style={{ padding: '6px 8px', color: '#8b8b8b' }}>
              {day1Reuse} reuse existing{day1New > 0 ? `, ${day1New} new staged` : ''}
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: 600, padding: '6px 8px' }}>Credentials</td>
            <td style={{ padding: '6px 8px', color: '#8b8b8b' }}>
              {credsReuse} reuse existing{credsNew > 0 ? `, ${credsNew} newly entered` : ''}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Per-node detail */}
      <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Per-node</p>
      <div style={{ maxHeight: '32vh', overflow: 'auto', border: '1px solid #4d4d4d', borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Node</th>
              <th style={{ padding: '6px 8px' }}>Current ver.</th>
              <th style={{ padding: '6px 8px' }}>CIQ</th>
              <th style={{ padding: '6px 8px' }}>Day-1</th>
              <th style={{ padding: '6px 8px' }}>Credentials</th>
            </tr>
          </thead>
          <tbody>
            {selected.map((n) => (
              <tr key={n.id} style={{ borderTop: '1px solid #3a3a3a' }}>
                <td style={{ padding: '6px 8px' }}>{n.nodeName}</td>
                <td style={{ padding: '6px 8px', color: '#8b8b8b' }}>{n.currentVersion ?? '—'}</td>
                <td style={{ padding: '6px 8px' }}>{reuseNewTag(!!n.hasStagedCiq)}</td>
                <td style={{ padding: '6px 8px' }}>{reuseNewTag(!!n.hasStagedDay1)}</td>
                <td style={{ padding: '6px 8px' }}>{reuseNewTag(n.credsProvided)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ReactEDSWrapper>,
    document.body
  );
}
